import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Check, ChevronRight, Shield, TrendingUp, DollarSign, Users, Award, Zap, Star, Crown, Diamond } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function InvestmentPlans() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const plans = [
    {
      name: "Standard Plan",
      price: "$1,000",
      multiplier: "10x",
      duration: "21 days",
      features: [
        "10x Profit in 21 days",
        "2% Welcome bonus",
        "$1,000 earn $10,000",
        "$2,000 earn $20,000",
        "$3,000 earn $30,000",
        "$4,000 earn $40,000",
        "$5,000 earn $50,000"
      ],
      type: "Basic portfolio",
      support: "24/7 Email Support",
      color: "blue",
      icon: Star
    },
    {
      name: "Premium Plan",
      price: "$6,000",
      multiplier: "15x",
      duration: "21 days",
      features: [
        "15x Profit in 21 days",
        "3% Welcome bonus",
        "$6,000 earn $90,000",
        "$7,000 earn $105,000",
        "$8,000 earn $120,000",
        "$9,000 earn $135,000",
        "$10,000 earn $150,000"
      ],
      type: "Enhanced portfolio",
      support: "24/7 Priority Chat Support",
      popular: true,
      color: "purple",
      icon: Crown
    },
    {
      name: "Advanced Plan",
      price: "$10,000",
      multiplier: "20x",
      duration: "21 days",
      features: [
        "20x Profit in 21 days",
        "5% Welcome bonus",
        "$10,000 earn $200,000",
        "$11,000 earn $220,000",
        "$12,000 earn $240,000",
        "$13,000 earn $260,000",
        "$14,000 earn $280,000",
        "VIP Account Manager",
        "Priority Withdrawals",
        "Exclusive Market Analysis"
      ],
      type: "VIP Wealth Management",
      support: "Dedicated Account Manager",
      color: "cyan",
      icon: Diamond
    },
    {
      name: "Platinum Plan",
      price: "$25,000",
      multiplier: "25x",
      duration: "21 days",
      features: [
        "25x Profit in 21 days",
        "7% Welcome bonus",
        "$25,000 earn $625,000",
        "$30,000 earn $750,000",
        "$35,000 earn $875,000",
        "$40,000 earn $1,000,000",
        "$45,000 earn $1,125,000",
        "Elite Account Manager",
        "Instant Withdrawals",
        "Advanced Market Analysis",
        "Private Investment Events",
        "Personalized Portfolio Strategy"
      ],
      type: "Elite Wealth Management",
      support: "24/7 Personal Concierge",
      color: "green",
      icon: Crown
    },
    {
      name: "Diamond Plan",
      price: "$50,000",
      multiplier: "30x",
      duration: "21 days",
      features: [
        "30x Profit in 21 days",
        "10% Welcome bonus",
        "$50,000 earn $1,500,000",
        "$60,000 earn $1,800,000",
        "$70,000 earn $2,100,000",
        "$80,000 earn $2,400,000",
        "$90,000 earn $2,700,000",
        "Executive Account Manager",
        "Instant Priority Withdrawals",
        "Expert Market Analysis",
        "Exclusive Investment Events",
        "Custom Portfolio Strategy",
        "Global Investment Opportunities",
        "Wealth Preservation Consulting"
      ],
      type: "Executive Wealth Management",
      support: "Dedicated Executive Team",
      color: "yellow",
      icon: Diamond
    }
  ];

  const stats = [
    { value: '$100M+', label: 'Assets Under Management', icon: DollarSign },
    { value: '15,000+', label: 'Happy Investors', icon: Users },
    { value: '94%', label: 'Success Rate', icon: Award }
  ];

  const testimonials = [
    {
      text: "I've tried other platforms, but this one really delivers on transparency and performance. My returns have been impressive!",
      author: "Irene Blimbing",
      rating: 5
    },
    {
      text: "The user interface is simple, and I love how my investments are laid out so clearly. Highly recommended!",
      author: "Michael Bedengan",
      rating: 5
    },
    {
      text: "The personalized investment updates are a game-changer. I always know exactly where my money stands!",
      author: "David Knox",
      rating: 5
    },
    {
      text: "Investing has never been easier! My portfolio has grown steadily, and the dashboard keeps me informed every step of the way.",
      author: "Angelina Kepanjen",
      rating: 5
    }
  ];

  const handlePlanSelect = (plan) => {
    localStorage.setItem('selectedPlan', JSON.stringify(plan));
    if (currentUser) {
      navigate('/dashboard?tab=deposit');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />

      {/* Hero Section - Unique Design */}
      <section className="relative pt-8 md:pt-20 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto py-20">
      <motion.div 
              className="inline-block mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 font-semibold text-sm border border-blue-500/30"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              💰 Choose Your Investment Path
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-8 leading-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Your Path to
              </span>
              <br />
              <span className="text-white">
                Financial Freedom
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl mb-12 text-gray-300 leading-relaxed max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Secure your financial future with expert-backed cryptocurrency investment strategies tailored for long-term growth and stability.
            </motion.p>

            <motion.button
              onClick={() => handlePlanSelect(plans[0])}
              className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center mx-auto space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <span>Start Your Investment Journey</span>
              <ChevronRight className="h-5 w-5" />
            </motion.button>
            </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-700">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
            <motion.div 
                  key={index}
                  className="text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
            >
                  <Icon className="mx-auto mb-4 text-blue-400" size={48} />
                  <div className="text-3xl font-bold mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
            </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Plans */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Choose Your Investment Plan
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Select the perfect investment plan that matches your financial goals and risk tolerance.
            </p>
          </div>

          {/* New Layout: Featured Plan in Center, Others Around */}
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            {/* Standard Plan - Left Side */}
            <div className="lg:col-span-3">
              <motion.div
                className="relative card-glass p-6 rounded-2xl card-hover h-full"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mb-3">
                    <Star className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Standard Plan</h3>
                  <div className="text-3xl font-bold text-white mb-2">$1,000</div>
                  <div className="text-sm text-gray-400 mb-3">Basic portfolio</div>
                  <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-3 py-1 rounded-full inline-block">
                    <span className="text-xl font-bold text-blue-400">10x</span>
                    <span className="text-gray-400 ml-1 text-sm">in 21 days</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {plans[0].features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Check className="text-green-400 flex-shrink-0" size={16} />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <motion.button
                  onClick={() => handlePlanSelect(plans[0])}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-xl text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Choose Standard
                </motion.button>
              </motion.div>
            </div>

            {/* Premium Plan - Center Featured */}
            <div className="lg:col-span-6">
              <motion.div
                className="relative card-glass p-8 rounded-2xl card-hover ring-2 ring-purple-500 scale-105"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>

                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-600 to-purple-500 rounded-full mb-4">
                    <Crown className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2">Premium Plan</h3>
                  <div className="text-5xl font-bold text-white mb-2">$6,000</div>
                  <div className="text-xl text-gray-400 mb-4">Enhanced portfolio</div>
                  <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-6 py-3 rounded-full inline-block">
                    <span className="text-3xl font-bold text-blue-400">15x</span>
                    <span className="text-gray-400 ml-3 text-lg">in 21 days</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {plans[1].features.slice(0, 6).map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <Check className="text-green-400 flex-shrink-0" size={20} />
                      <span className="text-gray-300">{feature}</span>
                  </div>
                  ))}
                </div>

                <motion.button
                  onClick={() => handlePlanSelect(plans[1])}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-xl text-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Choose Premium
                </motion.button>
              </motion.div>
          </div>

            {/* Advanced Plan - Right Side */}
            <div className="lg:col-span-3">
              <motion.div
                className="relative card-glass p-6 rounded-2xl card-hover h-full"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full mb-3">
                    <Diamond className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Advanced Plan</h3>
                  <div className="text-3xl font-bold text-white mb-2">$10,000</div>
                  <div className="text-sm text-gray-400 mb-3">VIP Wealth Management</div>
                  <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-3 py-1 rounded-full inline-block">
                    <span className="text-xl font-bold text-blue-400">20x</span>
                    <span className="text-gray-400 ml-1 text-sm">in 21 days</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {plans[2].features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Check className="text-green-400 flex-shrink-0" size={16} />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <motion.button
                  onClick={() => handlePlanSelect(plans[2])}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-xl text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Choose Advanced
                </motion.button>
              </motion.div>
            </div>
          </div>

          {/* Bottom Row: Platinum and Diamond Plans */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Platinum Plan */}
            <motion.div
              className="relative card-glass p-8 rounded-2xl card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-600 to-green-500 rounded-full mb-4">
                  <Crown className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Platinum Plan</h3>
                <div className="text-4xl font-bold text-white mb-2">$25,000</div>
                <div className="text-lg text-gray-400 mb-4">Elite Wealth Management</div>
                <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-4 py-2 rounded-full inline-block">
                  <span className="text-2xl font-bold text-blue-400">25x</span>
                  <span className="text-gray-400 ml-2">in 21 days</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {plans[3].features.slice(0, 6).map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <Check className="text-green-400 flex-shrink-0" size={20} />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
          </div>

              <motion.button
                onClick={() => handlePlanSelect(plans[3])}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-xl"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Choose Platinum
              </motion.button>
            </motion.div>

            {/* Diamond Plan */}
            <motion.div
              className="relative card-glass p-8 rounded-2xl card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-full mb-4">
                  <Diamond className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Diamond Plan</h3>
                <div className="text-4xl font-bold text-white mb-2">$50,000</div>
                <div className="text-lg text-gray-400 mb-4">Executive Wealth Management</div>
                <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 px-4 py-2 rounded-full inline-block">
                  <span className="text-2xl font-bold text-blue-400">30x</span>
                  <span className="text-gray-400 ml-2">in 21 days</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {plans[4].features.slice(0, 6).map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <Check className="text-green-400 flex-shrink-0" size={20} />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <motion.button
                onClick={() => handlePlanSelect(plans[4])}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-xl"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Choose Diamond
              </motion.button>
              </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-r from-slate-700 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Why Choose Onchainvest Capital?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the difference with our proven investment strategies and exceptional service.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="card-glass p-8 rounded-2xl text-center card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Shield className="mx-auto mb-6 text-blue-400" size={48} />
              <h3 className="text-xl font-bold text-white mb-4">Secure & Regulated</h3>
              <p className="text-gray-300">
                Your investments are protected by industry-leading security measures and regulatory compliance.
              </p>
            </motion.div>

            <motion.div
              className="card-glass p-8 rounded-2xl text-center card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <TrendingUp className="mx-auto mb-6 text-purple-400" size={48} />
              <h3 className="text-xl font-bold text-white mb-4">Proven Performance</h3>
              <p className="text-gray-300">
                Our investment strategies have consistently delivered exceptional returns for our clients.
              </p>
            </motion.div>

                <motion.div
              className="card-glass p-8 rounded-2xl text-center card-hover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Zap className="mx-auto mb-6 text-cyan-400" size={48} />
              <h3 className="text-xl font-bold text-white mb-4">Fast & Easy</h3>
              <p className="text-gray-300">
                Start investing in minutes with our streamlined process and user-friendly platform.
              </p>
                </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              What Our Investors Say
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Join thousands of satisfied investors who have achieved their financial goals with us.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="card-glass p-8 rounded-2xl card-hover"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400" size={20} />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-white">{testimonial.author}</div>
                    <div className="text-sm text-gray-400">Verified Investor</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Investment Journey?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of successful investors who have already started building their wealth with Onchainvest Capital.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={() => handlePlanSelect(plans[0])}
              className="bg-white text-slate-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
              Start Investing Now
            </motion.button>
            <Link to="/contact">
              <button className="border-2 border-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
                Contact Our Team
              </button>
                </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default InvestmentPlans;